import { SubaccountDto } from "@common/dto/financialAccount/subaccount.dto";
import { assertRelationLoaded } from "@common/helpers/orm";
import {
    FinancialAccount,
    FinancialAccountBase,
    FinancialAccountSourceType,
    FinancialAccountType,
} from "../../types/financialAccount";
import { isoStringOrNull, unpackDateOrNull, valueOrNull } from "../dto-helpers";
import { EntityDto } from "../entity/entity.dto";
import {
    IntegrationAccountDto,
    IntegrationAccountDtoInput,
} from "./integration-account.dto";

interface FinancialAccountDtoOptions {
    includeIntegrationAccounts?: boolean;
}

/**
 * Input type for FinancialAccountDto constructor.
 * Accepts Base type from entity classes - entity relation may or may not be loaded.
 */
export type FinancialAccountDtoInput = FinancialAccountBase;

export class FinancialAccountDto {
    static deserialize(dto: FinancialAccountDto): FinancialAccount {
        const { integrationAccounts, subaccounts, ...rest } = dto;

        const response: FinancialAccount = {
            ...rest,
            balanceSyncedAt: unpackDateOrNull(dto.balanceSyncedAt),
            transactionsSyncedAt: unpackDateOrNull(dto.transactionsSyncedAt),
            continuousReconciliationEnabledAt: unpackDateOrNull(
                dto.continuousReconciliationEnabledAt,
            ),
            createdAt: unpackDateOrNull(dto.createdAt),
            entity: valueOrNull(dto.entity, EntityDto.deserialize),
        };

        if (integrationAccounts) {
            response.integrationAccounts = integrationAccounts.map(
                (integrationAccount) =>
                    IntegrationAccountDto.deserialize(integrationAccount),
            );
        }

        if (subaccounts) {
            response.subaccounts = subaccounts.map((subaccount) =>
                SubaccountDto.deserialize(subaccount),
            );
        }

        return response;
    }
    id: number;
    balance: number | null;
    balanceSyncedAt: string | null;
    transactionsSyncedAt: string | null;
    continuousReconciliationEnabledAt: string | null;
    createdAt: string | null;
    entityId: number | null;
    entity: EntityDto | null;
    name: string | null;
    isBusiness: boolean | null;
    order: number;
    sourceType: FinancialAccountSourceType;
    routingNumber: string | null;
    wireRoutingNumber: string | null;
    accountNumberMask: string | null;
    disabled: boolean;
    financialType: FinancialAccountType;
    integrationAccounts?: IntegrationAccountDto[];
    institutionName: string | null;
    subaccounts?: SubaccountDto[];
    bookkeepingStartDate: string | null;
    bookkeepingEndDate: string | null;

    constructor(
        financialAccount: FinancialAccountDtoInput,
        { includeIntegrationAccounts = true }: FinancialAccountDtoOptions = {},
    ) {
        this.id = financialAccount.id;
        this.balance = financialAccount.balance;
        this.balanceSyncedAt = isoStringOrNull(
            financialAccount.balanceSyncedAt,
        );
        this.transactionsSyncedAt = isoStringOrNull(
            financialAccount.transactionsSyncedAt,
        );
        this.continuousReconciliationEnabledAt = isoStringOrNull(
            financialAccount.continuousReconciliationEnabledAt,
        );
        this.createdAt = isoStringOrNull(financialAccount.createdAt);
        this.entityId = financialAccount.entityId;
        // entity may be undefined (not loaded) or null (loaded but empty)
        // In both cases, serialize as null
        this.entity = financialAccount.entity
            ? new EntityDto(financialAccount.entity)
            : null;
        this.name = financialAccount.name;
        this.isBusiness = financialAccount.isBusiness;
        this.order = financialAccount.order;
        this.sourceType = financialAccount.sourceType;
        this.routingNumber = financialAccount.routingNumber;
        this.wireRoutingNumber = financialAccount.wireRoutingNumber;
        this.accountNumberMask = financialAccount.accountNumberMask;
        this.disabled = financialAccount.disabled;
        this.financialType = financialAccount.financialType;
        this.institutionName = financialAccount.institutionName;
        this.bookkeepingStartDate =
            financialAccount.bookkeepingStartDate ?? null;
        this.bookkeepingEndDate = financialAccount.bookkeepingEndDate ?? null;

        this.subaccounts = financialAccount.subaccounts
            ? financialAccount.subaccounts.map(
                  (subaccount) => new SubaccountDto(subaccount),
              )
            : [];

        if (
            financialAccount.integrationAccounts &&
            includeIntegrationAccounts
        ) {
            this.integrationAccounts = financialAccount.integrationAccounts.map(
                (integrationAccount) =>
                    new IntegrationAccountDto(
                        {
                            ...integrationAccount,
                            financialAccount,
                        } as IntegrationAccountDtoInput,
                        { nestedInFinancialAccount: true },
                    ),
            );
        }
    }
}

/**
 * FinancialAccountWithEntityDto - serializes financial account with entity relation guaranteed loaded.
 * Use in places where entity must be present (e.g. after explicit relation loading).
 */
export class FinancialAccountWithEntityDto extends FinancialAccountDto {
    constructor(
        financialAccount: FinancialAccountDtoInput,
        options?: FinancialAccountDtoOptions,
    ) {
        assertRelationLoaded(financialAccount, "entity");
        super(financialAccount, options);
    }
}
